/**
 * GİB standart e-Arşiv / e-Fatura görünümü (A4).
 *
 * Kolaysoft'un "Önizle / İndir / Yazdır" çıktısıyla aynı düzen: satıcı bloğu,
 * GİB logosu, karekod, SAYIN bloğu, özelleştirme tablosu, ETTN, kalemler,
 * toplamlar, vergi istisna ve notlar kutusu.
 *
 * Yazdırma ve PDF için stiller satır içi (inline) tutuldu; bileşenin HTML'i
 * iframe'e kopyalandığında görünüm bozulmaz.
 */

import React from 'react';
import gibLogo from './gib-logo.png';

const FONT = "Arial, Helvetica, 'Liberation Sans', sans-serif";
const ink = '#000';

const fmtMoney = (v) =>
  `${Number(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL`;
const fmtUnitPrice = (v) =>
  `${Number(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 4 })} TL`;
const fmtQty = (q, unit) => `${Number(q || 0).toLocaleString('tr-TR', { maximumFractionDigits: 3 })} ${unit}`;
const fmtRate = (r) => `%${Number(r || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const thickRule = { borderTop: `3px solid ${ink}`, margin: 0 };

const s = {
  page: {
    position: 'relative',
    width: 794,
    minHeight: 1123,
    boxSizing: 'border-box',
    padding: '42px 48px 36px',
    background: '#fff',
    color: ink,
    fontFamily: FONT,
    fontSize: 11.5,
    lineHeight: 1.35
  },
  cell: { border: `1px solid ${ink}`, padding: '3px 4px', verticalAlign: 'middle' },
  headCell: { border: `1px solid ${ink}`, padding: '3px 4px', fontWeight: 700, verticalAlign: 'middle', textAlign: 'left' },
  metaLabel: { border: '1px solid #9ca3af', padding: '2px 4px', fontWeight: 700, width: 150 },
  metaValue: { border: '1px solid #9ca3af', padding: '2px 4px', width: 150 }
};

const Line = ({ children }) => <div style={{ minHeight: 15 }}>{children}</div>;

const splitNote = (note) => {
  const i = note.indexOf(':');
  return i > 0 ? [note.slice(0, i + 1), note.slice(i + 1)] : ['', note];
};

export default function GibInvoiceDocument({ model, qrDataUrl }) {
  if (!model) return null;
  const { seller, buyer, meta, totals } = model;
  const title = model.documentType === 'EFATURA' ? 'e-Fatura' : 'e-Arşiv Fatura';
  const buyerPlace = [buyer.district, buyer.city].filter(Boolean).join('/ ');
  const sellerPlace = `${seller.address ? `${seller.address} ` : ''}${seller.district || ''}/ ${seller.city || ''}`;

  return (
    <div style={s.page} className="gib-invoice-page">
      {model.watermark ? (
        <div
          style={{
            position: 'absolute',
            top: 430,
            left: 0,
            right: 0,
            textAlign: 'center',
            fontSize: 120,
            fontWeight: 900,
            color: 'rgba(220, 38, 38, 0.10)',
            transform: 'rotate(-28deg)',
            pointerEvents: 'none',
            letterSpacing: 12
          }}
        >
          {model.watermark}
        </div>
      ) : null}

      {/* Üst: satıcı · logo · karekod */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ width: 370, paddingTop: 20 }}>
          <div style={thickRule} />
          <div style={{ padding: '8px 0' }}>
            <Line>{seller.title}</Line>
            <Line>{seller.title}</Line>
            <Line />
            <Line>{sellerPlace}</Line>
            <Line>Vergi Dairesi: {seller.taxOffice}</Line>
            <Line>
              {seller.idLabel}: {seller.id}
            </Line>
            <Line>Ticaret Sicil No: {seller.tradeRegistry || '-'}</Line>
            {seller.mersisNo ? <Line>MERSİS No: {seller.mersisNo}</Line> : null}
          </div>
          <div style={thickRule} />
        </div>
        <div style={{ width: 150, textAlign: 'center', paddingTop: 22 }}>
          <img src={gibLogo} alt="GİB" style={{ width: 96, height: 96 }} />
          <div style={{ fontSize: 15, fontWeight: 700, marginTop: 10 }}>{title}</div>
        </div>
        <div style={{ width: 170, textAlign: 'right', paddingTop: 4 }}>
          {qrDataUrl ? (
            <img src={qrDataUrl} alt="Karekod" style={{ width: 150, height: 150 }} />
          ) : (
            <div
              style={{
                display: 'inline-flex',
                width: 150,
                height: 150,
                border: '1px dashed #9ca3af',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10.5,
                color: '#6b7280',
                textAlign: 'center',
                padding: 8,
                boxSizing: 'border-box'
              }}
            >
              Karekod fatura GİB'e gönderildiğinde oluşur
            </div>
          )}
        </div>
      </div>

      {/* Alıcı · özelleştirme tablosu */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 22 }}>
        <div style={{ width: 370 }}>
          <div style={thickRule} />
          <div style={{ padding: '8px 0' }}>
            <Line>
              <b>SAYIN</b>
            </Line>
            <Line>{buyer.title}</Line>
            <Line>{buyer.address}</Line>
            <Line>{buyerPlace}</Line>
            <Line>{buyer.country}</Line>
            <Line>Vergi Dairesi: {buyer.taxOffice}</Line>
            <Line>
              {buyer.idLabel}: {buyer.id}
            </Line>
          </div>
          <div style={thickRule} />
        </div>
        <table style={{ borderCollapse: 'collapse', fontSize: 11.5, marginBottom: 34 }}>
          <tbody>
            {[
              ['Özelleştirme No:', meta.customizationId],
              ['Senaryo:', meta.scenario],
              ['Fatura Tipi:', meta.invoiceType],
              ['Fatura No:', meta.number || 'Gönderimde verilir'],
              ['Fatura Tarihi:', `${meta.date}${meta.time ? ` ${meta.time}` : ''}`]
            ].map(([k, v]) => (
              <tr key={k}>
                <td style={s.metaLabel}>{k}</td>
                <td style={s.metaValue}>{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 10, marginBottom: 8 }}>
        <b>ETTN:</b>&nbsp;&nbsp;{model.ettn || (model.issued ? '' : 'Gönderimde verilir')}
      </div>

      {/* Kalemler */}
      <table style={{ width: '100%', borderCollapse: 'collapse', border: `2px solid ${ink}`, fontSize: 11 }}>
        <thead>
          <tr>
            {[
              ['Sıra No', 32],
              ['Ürün Kodu', 70],
              ['Mal Hizmet', 190],
              ['Miktar', 70],
              ['Birim Fiyat', 88],
              ['KDV Oranı', 64],
              ['KDV Tutarı', 88],
              ['Diğer Vergiler', 150],
              ['Mal Hizmet Tutarı', 104],
              ['Özel Matrah Nedeni', 90],
              ['Özel Matrah Tutarı', 64]
            ].map(([h, w]) => (
              <th key={h} style={{ ...s.headCell, width: w }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {model.linesMissing ? (
            <tr>
              <td colSpan={11} style={{ ...s.cell, padding: '14px 10px', textAlign: 'center', fontSize: 11.5 }}>
                Bu faturanın kalem ayrıntısı Kolaysoft Excel dökümünde bulunmuyor. Birebir görüntü için
                Kolaysoft'tan XML (UBL) dökümü yüklenmelidir. Tutarlar döküme göredir.
              </td>
            </tr>
          ) : (
            model.lines.map((l) => (
              <tr key={l.no}>
                <td style={s.cell}>{l.no}</td>
                <td style={s.cell}>{l.code}</td>
                <td style={s.cell}>{l.name}</td>
                <td style={{ ...s.cell, textAlign: 'right' }}>{fmtQty(l.quantity, l.unit)}</td>
                <td style={{ ...s.cell, textAlign: 'right' }}>{fmtUnitPrice(l.unitPrice)}</td>
                <td style={{ ...s.cell, textAlign: 'right' }}>{fmtRate(l.vatRate)}</td>
                <td style={{ ...s.cell, textAlign: 'right' }}>{fmtMoney(l.vatAmount)}</td>
                <td style={s.cell} />
                <td style={{ ...s.cell, textAlign: 'right' }}>{fmtMoney(l.net)}</td>
                <td style={{ ...s.cell, textAlign: 'right' }}>{l.ozelMatrahReason}</td>
                <td style={{ ...s.cell, textAlign: 'right' }}>
                  {l.ozelMatrahAmount != null ? Number(l.ozelMatrahAmount).toFixed(2) : ''}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Toplamlar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
        <table style={{ borderCollapse: 'separate', borderSpacing: '0 3px', fontSize: 11.5 }}>
          <tbody>
            {[
              ['Mal Hizmet Toplam Tutarı', totals.goodsTotal],
              ['Toplam İskonto', totals.discount],
              ['Hesaplanan GERÇEK USULDE KATMA DEĞER VERGİSİ(%0)', totals.vat0],
              ['Hesaplanan GERÇEK USULDE KATMA DEĞER VERGİSİ(%20)', totals.vat20],
              ['Vergiler Dahil Toplam Tutar', totals.taxInclusive],
              ['Ödenecek Tutar', totals.payable]
            ].map(([k, v]) => (
              <tr key={k}>
                <td
                  style={{
                    border: `2px solid ${ink}`,
                    padding: '2px 6px',
                    fontWeight: 700,
                    textAlign: 'right',
                    width: 290
                  }}
                >
                  {k}
                </td>
                <td style={{ border: `2px solid ${ink}`, padding: '2px 6px', textAlign: 'right', width: 110 }}>
                  {fmtMoney(v)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Notlar */}
      <div style={{ border: `2px solid ${ink}`, padding: '18px 26px', marginTop: 34, fontSize: 11.5 }}>
        {model.exemptionText ? (
          <div>
            <b>Vergi İstisna Muafiyet Sebebi:</b>
            {model.exemptionText}
          </div>
        ) : null}
        {model.notes.map((n, i) => {
          const [label, text] = splitNote(n);
          return (
            <div key={`${i}-${n}`}>
              {label ? <b>{label}</b> : null}
              {text}
            </div>
          );
        })}
        <div>
          <b>Not:</b>Yalnız {model.amountWords}
        </div>
      </div>

      {model.documentType === 'EARSIV' ? (
        <div style={{ marginTop: 6, fontSize: 11.5 }}>
          <div>e-Arşiv izni kapsamında elektronik ortamda iletilmiştir.</div>
          <div>
            Bu faturayı elektronik olarak görmek ve doğrulamak için <b>ebelge.gib.gov.tr</b> adresini ziyaret ediniz.
          </div>
        </div>
      ) : null}
    </div>
  );
}
